import React, { useState, useRef } from "react";
import { submitContact } from "../lib/api";
import Seo from "../components/Seo";
import { Phone, Mail, MapPin, Clock, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const startedAt = useRef(Date.now());

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitContact({ ...form, started_at: startedAt.current });
      setSent(true);
      toast.success("Mensaje enviado correctamente");
      setForm({ name: "", email: "", phone: "", company: "", message: "", website: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar el mensaje");
    } finally {
      setSending(false);
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "FABRIPAN",
    telephone: "+34 931739860",
    email: "info@fabripan.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Av. Setze, 5, Urbanización la Florida",
      addressLocality: "Santa Perpetua de la Moguda",
      postalCode: "08130", addressCountry: "ES",
    },
    openingHours: "Mo-Fr 08:00-15:00",
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <Seo title="Contacto" keywords="contacto fabripan, teléfono fabripan, fabricante equipamiento panadería Barcelona, Santa Perpètua de Mogoda" description="Contacte con FABRIPAN. Av. Setze, 5, Santa Perpetua de la Moguda. Tel +34 931739860. Soporte de lunes a viernes de 08:00 a 15:00 h." jsonLd={jsonLd} />
      <h1 className="font-heading font-bold text-4xl tracking-tighter">Contacto</h1>
      <p className="text-muted-foreground mt-2">¿Tiene un proyecto o necesita un presupuesto? Escríbanos.</p>

      <div className="grid lg:grid-cols-2 gap-10 mt-8">
        <div className="space-y-4">
          <div className="border border-border rounded-sm bg-white p-5 space-y-4">
            <div className="flex items-start gap-3"><MapPin className="h-5 w-5 text-brand shrink-0 mt-0.5" /><div><p className="font-medium">Dirección</p><p className="text-sm text-muted-foreground">Av. Setze, 5, Urbanización la Florida<br />08130 Santa Perpetua de la Moguda, España</p></div></div>
            <div className="flex items-start gap-3"><Phone className="h-5 w-5 text-brand shrink-0 mt-0.5" /><div><p className="font-medium">Teléfono</p><a href="tel:+34931739860" className="text-sm text-brand hover:underline" data-testid="contact-phone-link">+34 931 739 860</a></div></div>
            <div className="flex items-start gap-3"><Mail className="h-5 w-5 text-brand shrink-0 mt-0.5" /><div><p className="font-medium">Email</p><a href="mailto:info@fabripan.com" className="text-sm text-brand hover:underline" data-testid="contact-email-link">info@fabripan.com</a></div></div>
            <div className="flex items-start gap-3"><Clock className="h-5 w-5 text-brand shrink-0 mt-0.5" /><div><p className="font-medium">Horario de soporte</p><p className="text-sm text-muted-foreground">Lunes a viernes de 08:00 a 15:00 horas.</p></div></div>
          </div>
          <div className="rounded-sm overflow-hidden border border-border h-64">
            <iframe title="Mapa FABRIPAN" width="100%" height="100%" loading="lazy" style={{ border: 0 }}
              src="https://www.google.com/maps?q=Avinguda+Setze+5+Santa+Perpetua+de+Mogoda&output=embed" />
          </div>
        </div>

        {sent ? (
          <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-8 flex flex-col items-center justify-center text-center">
            <Send className="h-10 w-10 text-emerald-600" />
            <p className="font-heading font-semibold text-lg mt-3">¡Gracias por su mensaje!</p>
            <p className="text-sm text-muted-foreground mt-1">Le responderemos lo antes posible en horario de soporte.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-border rounded-sm bg-white p-5 space-y-4">
            <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-sm font-medium block mb-1">Nombre *</label><input required value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="contact-name" /></div>
              <div><label className="text-sm font-medium block mb-1">Empresa</label><input value={form.company} onChange={(e) => set("company", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="contact-company" /></div>
              <div><label className="text-sm font-medium block mb-1">Email *</label><input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="contact-email" /></div>
              <div><label className="text-sm font-medium block mb-1">Teléfono</label><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="contact-phone" /></div>
            </div>
            <div><label className="text-sm font-medium block mb-1">Mensaje *</label><textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" data-testid="contact-message" /></div>
            <button type="submit" disabled={sending} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-sm transition-colors" data-testid="contact-submit">
              {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Enviando...</> : <><Send className="h-4 w-4" /> Enviar mensaje</>}
            </button>
            <p className="text-xs text-muted-foreground">Sus datos se usan solo para atender su consulta. Formulario protegido contra spam.</p>
          </form>
        )}
      </div>
    </div>
  );
}
